/**
 * POST/SSE reader shared by browser fetch and Expo's native streaming fetch.
 * Drafts are transient; only the result event is an authoritative response.
 */
export async function readChatResponse(
  response: Response,
  onDraft: (text: string) => void,
): Promise<Response> {
  if (!response.headers.get("content-type")?.includes("text/event-stream")) return response;
  const reader = response.body?.getReader();
  if (!reader) throw new Error("Live reply is unavailable. Please try again.");
  const decoder = new TextDecoder();
  let buffer = "";
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      void reader.cancel().catch(() => {});
      reject(new Error("The reply timed out. Please try again."));
    }, 150_000);
  });
  try {
    while (true) {
      const { value, done } = await Promise.race([reader.read(), expired]);
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, "\n");
      if (buffer.length > 1_000_000) throw new Error("The reply could not be read.");
      let end: number;
      while ((end = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, end);
        buffer = buffer.slice(end + 2);
        const data = frame.split("\n").filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart()).join("\n");
        if (!data) continue;
        const event = JSON.parse(data);
        if (event.type === "draft" && typeof event.text === "string") onDraft(event.text);
        if (
          event.type === "result" && Number.isInteger(event.status)
          && event.status >= 200 && event.status <= 599
          && event.payload !== undefined
        ) {
          return new Response(JSON.stringify(event.payload), {
            status: event.status,
            headers: { "Content-Type": "application/json" },
          });
        }
      }
      if (done) throw new Error("The reply was interrupted. Please try again.");
    }
  } finally {
    clearTimeout(timer);
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
